import { parseLineString } from '@/services/lineStringService'
import { getTrackLineInsertionProjection } from '@/utils/trackLineInsertion'

const EARTH_RADIUS_METERS = 6_371_000
const DEG_TO_RAD = Math.PI / 180
const SPARSE_TRACK_MIN_HELPER_POINTS = 1
const SPARSE_TRACK_MAX_TARGET_SEGMENTS = 18
export const DEFAULT_SPARSE_TRACK_MAX_HELPER_POINTS = 100

const haversineMeters = (a: [number, number], b: [number, number]): number => {
  const lat1 = a[1] * DEG_TO_RAD
  const lat2 = b[1] * DEG_TO_RAD
  const dLat = lat2 - lat1
  const dLon = (b[0] - a[0]) * DEG_TO_RAD
  const sinLat = Math.sin(dLat / 2)
  const sinLon = Math.sin(dLon / 2)
  const h = sinLat * sinLat + Math.cos(lat1) * Math.cos(lat2) * sinLon * sinLon
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(h))
}

const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value))

export type TrackEditMaterializationPlan = {
  anchorIndices: number[]
  anchorCoords: [number, number][]
  segmentCoords: [number, number][][]
}

export const buildTrackEditMaterializationPlan = (
  lineString: string | undefined,
): TrackEditMaterializationPlan | null => {
  if (!lineString) return null
  const polyline = parseLineString(lineString)
  if (polyline.length < 3) return null

  const cumulativeDistances = [0]
  let totalDistance = 0

  for (let i = 1; i < polyline.length; i += 1) {
    totalDistance += haversineMeters(polyline[i - 1], polyline[i])
    cumulativeDistances.push(totalDistance)
  }

  if (totalDistance <= 0) return null

  const targetSegments = clamp(Math.round(totalDistance / 1_500), 4, 18)
  const lastIndex = polyline.length - 1
  const anchorIndices = [0]
  let lastChosenIndex = 0

  for (let segmentIndex = 1; segmentIndex < targetSegments; segmentIndex += 1) {
    const threshold = (totalDistance * segmentIndex) / targetSegments
    let candidateIndex = cumulativeDistances.findIndex((distance) => distance >= threshold)

    if (candidateIndex < 0) {
      candidateIndex = lastIndex
    }

    if (candidateIndex <= lastChosenIndex) {
      candidateIndex = lastChosenIndex + 1
    }

    if (candidateIndex >= lastIndex) {
      break
    }

    anchorIndices.push(candidateIndex)
    lastChosenIndex = candidateIndex
  }

  if (anchorIndices[anchorIndices.length - 1] !== lastIndex) {
    anchorIndices.push(lastIndex)
  }

  if (anchorIndices.length < 3) return null

  const segmentCoords: [number, number][][] = []
  for (let i = 0; i < anchorIndices.length - 1; i += 1) {
    const segment = polyline.slice(anchorIndices[i], anchorIndices[i + 1] + 1)
    if (segment.length >= 2) {
      segmentCoords.push(segment)
    }
  }

  if (segmentCoords.length < 2) return null

  return {
    anchorIndices,
    anchorCoords: anchorIndices.map((index) => polyline[index]),
    segmentCoords,
  }
}

type PolylineDistanceState = {
  polyline: [number, number][]
  cumulativeDistances: number[]
  totalDistance: number
}

const buildPolylineDistanceState = (
  lineString: string | undefined,
): PolylineDistanceState | null => {
  if (!lineString) return null
  const polyline = parseLineString(lineString)
  if (polyline.length < 2) return null

  const cumulativeDistances = [0]
  let totalDistance = 0

  for (let i = 1; i < polyline.length; i += 1) {
    totalDistance += haversineMeters(polyline[i - 1], polyline[i])
    cumulativeDistances.push(totalDistance)
  }

  if (totalDistance <= 0) return null

  return {
    polyline,
    cumulativeDistances,
    totalDistance,
  }
}

const interpolateAlongPolyline = (
  polyline: [number, number][],
  cumulativeDistances: number[],
  distanceMeters: number,
): [number, number] => {
  if (distanceMeters <= 0) return polyline[0]
  const lastIndex = cumulativeDistances.length - 1
  if (distanceMeters >= cumulativeDistances[lastIndex]) return polyline[lastIndex]

  for (let i = 1; i < cumulativeDistances.length; i += 1) {
    if (distanceMeters > cumulativeDistances[i]) continue

    const previousDistance = cumulativeDistances[i - 1]
    const segmentDistance = cumulativeDistances[i] - previousDistance
    if (segmentDistance <= 0) return polyline[i]

    const ratio = (distanceMeters - previousDistance) / segmentDistance
    const from = polyline[i - 1]
    const to = polyline[i]

    return [from[0] + (to[0] - from[0]) * ratio, from[1] + (to[1] - from[1]) * ratio]
  }

  return polyline[lastIndex]
}

export const getSuggestedSparseTrackHelperPointCount = (lineString: string | undefined): number => {
  const plan = buildTrackEditMaterializationPlan(lineString)
  if (!plan) return SPARSE_TRACK_MIN_HELPER_POINTS

  return (
    clamp(
      plan.anchorCoords.length - 2,
      SPARSE_TRACK_MIN_HELPER_POINTS,
      SPARSE_TRACK_MAX_TARGET_SEGMENTS - 1,
    ) * 2
  )
}

export const buildSparseTrackHelperCoords = (
  lineString: string | undefined,
  helperPointCount: number,
  maxHelperPointCount: number = DEFAULT_SPARSE_TRACK_MAX_HELPER_POINTS,
): [number, number][] | null => {
  const state = buildPolylineDistanceState(lineString)
  if (!state) return null

  const clampedHelperPointCount = clamp(
    Math.round(helperPointCount),
    SPARSE_TRACK_MIN_HELPER_POINTS,
    Math.max(SPARSE_TRACK_MIN_HELPER_POINTS, maxHelperPointCount),
  )
  const totalSegments = clampedHelperPointCount + 1
  const helperCoords: [number, number][] = []

  for (let index = 1; index <= clampedHelperPointCount; index += 1) {
    const threshold = (state.totalDistance * index) / totalSegments
    helperCoords.push(
      interpolateAlongPolyline(state.polyline, state.cumulativeDistances, threshold),
    )
  }

  return helperCoords
}

export const getMaterializedTrackInsertIndex = (
  lineString: string | undefined,
  plan: Pick<TrackEditMaterializationPlan, 'anchorIndices'>,
  lat: number,
  lon: number,
): number | null => {
  const projection = getTrackLineInsertionProjection(lineString, lat, lon)
  if (!projection) return null

  const pivotBeforeIndex = projection.beforeVertices.length - 1
  if (pivotBeforeIndex < 0) return null

  const trackInsertIndex = plan.anchorIndices.filter((index) => index <= pivotBeforeIndex).length
  return clamp(trackInsertIndex, 1, plan.anchorIndices.length - 1)
}

export const shouldMaterializeTrackForInsert = (
  lineString: string | undefined,
  lat: number,
  lon: number,
  thresholdMeters: number = 25,
): boolean => {
  const projection = getTrackLineInsertionProjection(lineString, lat, lon)
  if (!projection) return false

  return haversineMeters(projection.projected, [lon, lat]) > thresholdMeters
}
